import React, { useCallback, useRef, useState } from "react";
import styled from "styled-components";
import { FiCheck, FiMoreVertical, FiSliders, FiTrash2 } from "react-icons/fi";
import { useDismiss } from "../../../hooks/useDismiss";
import { menuKeys } from "../../../hooks/menuKeys";
import { Action } from "../shared";
import { useLanguage } from "../../../contexts/LanguageContext";
import EquipMenu from "./EquipMenu";
import ItemImage from "./ItemImage";
import { Saved, TEAM_CT, TEAM_T, decorationName, rarityKey, splitName } from "./data";

const Card = styled.li<{ $highlight?: boolean; $equipped: boolean }>`
  list-style: none;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 10px 10px 14px;
  min-width: 0;
  border-radius: 14px;
  background: ${(p) => p.theme.colors.surface};
  box-shadow: ${(p) =>
    p.$highlight
      ? `inset 0 0 0 2px ${p.theme.colors.primary}`
      : `inset 0 0 0 1px ${p.theme.colors.border}`};
  transition:
    background ${(p) => p.theme.motion.fast},
    box-shadow ${(p) => p.theme.motion.fast};
  &:hover,
  &:focus-within {
    background: ${(p) => p.theme.colors.raised};
    box-shadow: ${(p) =>
      p.$highlight
        ? `inset 0 0 0 2px ${p.theme.colors.primary}`
        : `inset 0 0 0 1px ${p.theme.colors.borderLight}`};
  }
  .stage {
    position: relative;
    aspect-ratio: 4 / 3;
    border-radius: 10px;
    padding: 10px;
    background:
      linear-gradient(to top, color-mix(in srgb, var(--rarity, #65778d) 16%, transparent), transparent 55%),
      radial-gradient(
        120% 90% at 50% 25%,
        ${(p) => p.theme.colors.surfaceHover},
        ${(p) => p.theme.colors.navigation}
      );
    box-shadow: inset 0 -3px 0 var(--rarity, #65778d);
    img {
      filter: drop-shadow(0 8px 10px #0007);
      transition: transform ${(p) => p.theme.motion.base} ${(p) => p.theme.motion.ease};
    }
  }
  &:hover .stage img {
    transform: scale(1.03);
  }
  .info {
    display: grid;
    gap: 2px;
    padding: 0 4px;
    min-width: 0;
  }
  .rarity {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 12px;
    line-height: 16px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.textMuted};
    &::before {
      content: "";
      flex: none;
      width: 8px;
      height: 8px;
      border-radius: 2px;
      background: var(--rarity, #65778d);
    }
  }
  h3 {
    font-size: 17px;
    line-height: 23px;
    font-weight: 700;
    letter-spacing: -0.01em;
    overflow-wrap: anywhere;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .model {
    font-size: 13px;
    line-height: 18px;
    color: ${(p) => p.theme.colors.textSecondary};
    overflow-wrap: anywhere;
    .wear {
      color: ${(p) => p.theme.colors.textMuted};
    }
  }
  .status {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 0 4px;
    min-width: 0;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    min-height: 26px;
    padding: 0 9px 0 7px;
    border-radius: 7px;
    font-size: 12px;
    font-weight: 650;
    white-space: nowrap;
    color: ${(p) => p.theme.colors.text};
    background: ${(p) => p.theme.colors.input};
    box-shadow: inset 0 0 0 1px ${(p) => p.theme.colors.border};
    svg {
      width: 13px;
      height: 13px;
      color: ${(p) => p.theme.colors.primary};
    }
    &.off {
      color: ${(p) => p.theme.colors.textMuted};
      font-weight: 550;
      &::before {
        content: "";
        width: 7px;
        height: 7px;
        margin: 0 2px;
        border-radius: 999px;
        box-shadow: inset 0 0 0 1.5px ${(p) => p.theme.colors.textMuted};
      }
    }
  }
  .deco {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    padding: 0 4px;
    min-width: 0;
    .thumb {
      flex: none;
      width: 38px;
      height: 30px;
      border-radius: 6px;
      background: ${(p) => p.theme.colors.navigation};
      box-shadow: inset 0 0 0 1px ${(p) => p.theme.colors.border};
    }
    .more-n {
      font-size: 12px;
      font-weight: 650;
      color: ${(p) => p.theme.colors.textMuted};
    }
    .none {
      font-size: 12px;
      color: ${(p) => p.theme.colors.textMuted};
    }
  }
  .actions {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 8px;
    margin-top: auto;
    padding-top: 2px;
    > button,
    .trigger {
      min-height: 44px;
      padding-inline: 10px;
      font-size: 14px;
      white-space: nowrap;
    }
  }
  .corner {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 5;
  }
  .more {
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border-radius: 9px;
    color: ${(p) => p.theme.colors.textSecondary};
    background: #10151bcc;
    box-shadow: inset 0 0 0 1px ${(p) => p.theme.colors.border};
    backdrop-filter: blur(4px);
    &:hover,
    &[aria-expanded="true"] {
      background: ${(p) => p.theme.colors.surfaceHover};
      color: ${(p) => p.theme.colors.text};
    }
  }
  .menu {
    position: absolute;
    right: 0;
    top: calc(100% + 6px);
    z-index: 30;
    min-width: 200px;
    padding: 6px;
    border-radius: 12px;
    background: ${(p) => p.theme.colors.surfaceHover};
    border: 1px solid ${(p) => p.theme.colors.border};
    box-shadow: 0 16px 40px #0009;
    button {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 10px;
      min-height: 44px;
      padding: 0 10px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      color: ${(p) => p.theme.colors.danger};
      text-align: left;
      &:hover,
      &:focus-visible {
        background: ${(p) => p.theme.colors.input};
      }
    }
  }
  @media (max-width: 560px) {
    padding: 8px 8px 10px;
    gap: 10px;
    h3 {
      font-size: 15px;
      line-height: 20px;
    }
    .actions {
      grid-template-columns: minmax(0, 1fr);
    }
    .deco .thumb {
      width: 32px;
      height: 26px;
    }
  }
`;

interface Props {
  saved: Saved;
  inventory: Saved[];
  highlight?: boolean;
  busy?: boolean;
  onEdit: () => void;
  onDelete: () => void;
  /** Быстро включить/выключить скин для стороны, не открывая редактор. */
  onToggleSide: (team: number) => Promise<void>;
}

export default function InventoryCard({
  saved,
  inventory,
  highlight,
  busy,
  onEdit,
  onDelete,
  onToggleSide,
}: Props) {
  const { t } = useLanguage();
  const { model, skin } = splitName(saved.item.name);
  const rk = rarityKey(saved.item);
  const ids = [...(saved.stickers || []).filter(Boolean), saved.charm].filter(Boolean);
  const [menu, setMenu] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const closeMenu = useCallback(() => setMenu(false), []);
  useDismiss(menu, closeMenu, box, trigger);
  const sides = [TEAM_T, TEAM_CT].filter((x) => saved.teams.includes(x));
  const wear = saved.condition ? t(`skins.wear.${saved.condition}`) : "";
  const shownDeco = ids.slice(0, 6);
  return (
    <Card
      $highlight={highlight}
      $equipped={sides.length > 0}
      style={{ "--rarity": saved.item.color } as React.CSSProperties}
    >
      <div className="stage">
        <ItemImage src={saved.item.image} alt="" />
        <div className="corner" ref={box}>
          <button
            ref={trigger}
            type="button"
            className="more"
            aria-haspopup="menu"
            aria-expanded={menu}
            aria-label={t("skins.more_actions", { name: saved.item.name })}
            title={t("skins.more_actions", { name: saved.item.name })}
            onClick={() => setMenu((v) => !v)}
          >
            <FiMoreVertical aria-hidden="true" />
          </button>
          {menu && (
            <div
              className="menu"
              role="menu"
              onKeyDown={menuKeys}
              ref={(el) => el?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()}
            >
              <button
                type="button"
                role="menuitem"
                aria-label={`${t("skins.delete")}: ${saved.item.name}`}
                onClick={() => {
                  closeMenu();
                  onDelete();
                }}
              >
                <FiTrash2 aria-hidden="true" />
                {t("skins.delete")}
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="info">
        {rk && <div className="rarity">{t(rk)}</div>}
        <h3 title={saved.item.name}>{skin || model}</h3>
        <div className="model">
          {skin ? model : ""}
          {skin && wear ? <span className="wear"> · {wear}</span> : null}
          {!skin && wear ? <span className="wear">{wear}</span> : null}
        </div>
      </div>
      <div className="status">
        {sides.length === 0 ? (
          <span className="chip off">{t("skins.status_none")}</span>
        ) : (
          sides.map((x) => (
            <span key={x} className="chip">
              <FiCheck aria-hidden="true" />
              {t(x === TEAM_T ? "skins.status_t" : "skins.status_ct")}
            </span>
          ))
        )}
      </div>
      {saved.item.kind === "weapon" && (
        <div className="deco" aria-label={t("skins.decorations")}>
          {shownDeco.length === 0 && <span className="none">{t("skins.empty_deco")}</span>}
          {shownDeco.map((id, i) => (
            <span
              key={`${id}-${i}`}
              className="thumb"
              title={decorationName(saved.decorations?.[id])}
            >
              <ItemImage
                src={saved.decorations?.[id]?.image}
                alt={decorationName(saved.decorations?.[id])}
              />
            </span>
          ))}
          {ids.length > shownDeco.length && (
            <span className="more-n">+{ids.length - shownDeco.length}</span>
          )}
        </div>
      )}
      <div className="actions">
        <EquipMenu saved={saved} inventory={inventory} busy={busy} onToggle={onToggleSide} />
        <Action
          type="button"
          onClick={onEdit}
          aria-label={`${t("skins.configure")}: ${saved.item.name}`}
        >
          <FiSliders aria-hidden="true" />
          {t("skins.configure_short")}
        </Action>
      </div>
    </Card>
  );
}
